import type { ReactNode } from "react";
import { use } from "react";
import { useReactToPrint } from "react-to-print";

import PrintContext from "@/utils/print-context";

import Button from "../base/Button";

interface PrintIconProps {
    color: string;
}

const PrintIcon = ({ color }: PrintIconProps): ReactNode => (
    <svg
        width="500pt"
        height="500pt"
        version="1.0"
        viewBox="0 0 500.000000 500.000000"
        xmlns="http://www.w3.org/2000/svg"
    >
        <g transform="translate(-75 500) scale(.1 -.1)" stroke={color}>
            <path d="m1902 5298c-9-9-12-127-12-470v-458h-287c-312 0-349-5-441-55-66-36-140-109-174-172-57-105-58-109-58-938 0-813 2-841 51-936 30-59 119-150 177-182 94-51 142-57 449-57h283v-459c0-406 2-461 16-475 14-15 140-16 1305-14 1134 3 1291 5 1299 18 6 9 10 202 10 473v457h278c300 0 344 6 443 56 61 31 134 101 172 166 58 100 58 90 55 974l-3 809-23 55c-46 112-137 203-252 252-53 22-68 23-362 26l-308 3v458c0 342-3 460-12 469-17 17-2589 17-2606 0zm2528-803v-735h-1225-1225v735 735h1225 1225v-735zm-2540-475v-260h-79c-71 0-82-2-95-22-20-28-20-33 0-52 14-14 163-16 1488-16 1131 0 1475 3 1484 12 18 18 14 56-7 68-11 5-51 10-90 10h-71v260 261l303-3 302-3 56-27c68-34 132-96 165-161 42-81 45-163 42-948l-3-734-23-57c-31-76-108-159-180-195-52-26-68-28-194-31l-138-4v444c0 332-3 447-12 456s-386 12-1634 12c-1462 0-1624-2-1638-16s-16-69-16-456v-440l-137 4c-137 3-139 3-203 39-78 43-139 113-169 194-21 56-21 62-19 859l3 801 33 67c39 79 112 149 187 178 45 17 78 19 348 19l297 1v-260zm2870-1490v-410h-120-120v274c0 236-2 277-16 290-14 14-145 16-1299 16s-1285-2-1299-16c-14-13-16-54-16-290v-274h-125-125v410 410h1560 1560v-410zm-330-640v-720h-1225-1225v720 720h1225 1225v-720z" />
            <path d="m3562 3488c-19-19-14-56 8-68 12-6 83-9 187-8l168 3 9 27c19 53 3 58-188 58-120 0-176-4-184-12z" />
            <path d="m4052 3488c-17-17-15-44 4-62 13-13 43-16 175-16 92 0 169 4 180 10 21 12 25 50 7 68-17 17-349 17-366 0z" />
            <path d="m2292 2348c-7-7-12-21-12-33 0-45 2-45 451-45 375 0 429 2 443 16 21 20 20 37-2 57-17 15-62 17-443 17-317 0-428-3-437-12z" />
            <path d="m2298 2059c-21-12-24-49-6-67 9-9 122-12 445-12h433l12 26c11 23 10 29-11 45-22 18-47 19-440 19-264 0-422-4-433-11z" />
            <path d="m2912 1768c-18-18-14-56 7-68 13-6 205-10 555-10 473 0 536 2 550 16 22 21 20 51-3 64-30 15-1093 14-1109-2z" />
            <path d="m2298 1479c-21-12-24-49-6-67 17-17 1719-17 1736 0s15 44-4 62c-14 14-105 16-862 16-557-1-853-4-864-11z" />
        </g>
    </svg>
);

const renderIcon = (color: string): ReactNode => <PrintIcon color={color} />;

/**
 * Toolbar button that triggers printing the map.
 *
 * @param props - The button props forwarded to the base Button component.
 * @returns The rendered print map button.
 */
const PrintMap = (props: React.ComponentProps<typeof Button>) => {
    const printContext = use(PrintContext);

    const handlePrint = useReactToPrint({
        contentRef: { current: printContext?.context },
        suppressErrors: true,
    });

    return (
        <Button
            {...props}
            disabled={false}
            onClick={handlePrint}
            renderIcon={renderIcon}
        />
    );
};

export default PrintMap;
